import React, { useState, useEffect, useRef } from 'react';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import CopyButton from '../common/CopyButton';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { formatTimestamp } from '../../utils/format';
import { shortenAddress } from '../../utils/wallet';

// Paginated all-users list with inline block/unblock + filter by blocked status.
const AllUsersList = () => {
  const { storage } = useContracts();
  const notify = useNotify();

  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'blocked' | 'inactive-pkg'
  const [busy, setBusy] = useState(null); // userId being blocked/unblocked
  const runIdRef = useRef(0);

  const load = async () => {
    const myRun = ++runIdRef.current;
    setLoading(true);
    try {
      const total = await storage.totalUsers();
      const totalNum = total.toNumber ? total.toNumber() : Number(total);
      if (myRun !== runIdRef.current) return;
      setTotalUsers(totalNum);

      // Serials are 1-indexed. Fetch reverse chronological (newest first)
      const startSerial = Math.max(1, totalNum - (page - 1) * pageSize - (pageSize - 1));
      const endSerial = Math.max(1, totalNum - (page - 1) * pageSize);
      if (startSerial > endSerial) { setUsers([]); setLoading(false); return; }

      const userIds = await storage.getUsersBySerialRange(startSerial, endSerial);
      if (myRun !== runIdRef.current) return;
      const ids = userIds.map((x) => x.toNumber ? x.toNumber() : Number(x)).filter((x) => x !== 0);

      const details = await Promise.all(ids.map(async (id) => {
        try {
          const u = await storage.users(id);
          return {
            id: u[1].toNumber ? u[1].toNumber() : Number(u[1]),
            wallet: u[0],
            referrerId: u[2].toNumber ? u[2].toNumber() : Number(u[2]),
            serialNumber: u[3].toNumber ? u[3].toNumber() : Number(u[3]),
            joinTime: u[4].toNumber ? u[4].toNumber() : Number(u[4]),
            isBlocked: u[5],
            topPackageId: u[8].toNumber ? u[8].toNumber() : Number(u[8])
          };
        } catch { return null; }
      }));
      if (myRun !== runIdRef.current) return;
      // Reverse so newest is first
      const cleaned = details.filter(Boolean).reverse();
      setUsers(cleaned);
    } catch (_) { /* ignore */ }
    finally {
      if (myRun === runIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [page, pageSize]);

  const toggleBlock = async (u) => {
    setBusy(u.id);
    const action = u.isBlocked ? 'Unblocking' : 'Blocking';
    const l = notify.loading(`${action} user ${u.id}...`);
    try {
      const tx = u.isBlocked ? await storage.unblockUser(u.id) : await storage.blockUser(u.id);
      notify.dismiss(l);
      const m = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(m);
      notify.success(`User ${u.id} ${u.isBlocked ? 'unblocked' : 'blocked'}`);
      await load();
    } catch (err) {
      notify.dismiss(l);
      notify.error(parseError(err));
    } finally {
      setBusy(null);
    }
  };

  const filtered = users.filter((u) => {
    if (filter === 'blocked') return u.isBlocked;
    if (filter === 'active') return !u.isBlocked && u.topPackageId > 0;
    if (filter === 'inactive-pkg') return !u.isBlocked && u.topPackageId === 0;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));

  return (
    <Card
      title="All Users"
      subtitle={`Total registered: ${totalUsers.toLocaleString()}`}
      glow
      right={
        <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
          {loading ? <MiniLoader /> : '↻ Refresh'}
        </button>
      }
    >
      {/* Filter chips */}
      <div className="flex gap-sm mb-md" style={{ flexWrap: 'wrap' }}>
        {[
          { id: 'all', label: 'All' },
          { id: 'active', label: 'Active' },
          { id: 'inactive-pkg', label: 'No Package' },
          { id: 'blocked', label: 'Blocked' }
        ].map((f) => (
          <button
            key={f.id}
            className={filter === f.id ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && users.length === 0 && (
        <div className="text-center" style={{ padding: 40 }}><MiniLoader /></div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">👤</div>
          <div>No users match the current filter on this page</div>
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Wallet</th>
                <th>Ref</th>
                <th>Pkg</th>
                <th>Joined</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id}>
                  <td style={{ fontWeight: 700 }}>{u.id}</td>
                  <td className="font-mono" style={{ fontSize: 12 }}>
                    <div className="flex items-center gap-sm">
                      {shortenAddress(u.wallet)}
                      <CopyButton text={u.wallet} label="" copiedLabel="✓" />
                    </div>
                  </td>
                  <td>{u.referrerId}</td>
                  <td>{u.topPackageId > 0 ? `P${u.topPackageId}` : '-'}</td>
                  <td style={{ fontSize: 12 }}>{formatTimestamp(u.joinTime).split(',')[0]}</td>
                  <td>
                    {u.isBlocked ? (
                      <span className="badge badge-error">Blocked</span>
                    ) : u.topPackageId > 0 ? (
                      <span className="badge badge-success">Active</span>
                    ) : (
                      <span className="badge badge-warning">No Pkg</span>
                    )}
                  </td>
                  <td>
                    <button
                      className={u.isBlocked ? 'btn btn-success btn-sm' : 'btn btn-error btn-sm'}
                      onClick={() => toggleBlock(u)}
                      disabled={busy !== null}
                      style={{ padding: '4px 10px', fontSize: 11 }}
                    >
                      {busy === u.id ? <MiniLoader /> : (u.isBlocked ? 'Unblock' : 'Block')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalUsers > pageSize && (
        <div className="flex flex-between mt-md items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-sm items-center">
            <select
              className="input"
              style={{ padding: '6px 10px', fontSize: 12, width: 'auto' }}
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1 || loading}>
              ‹ Prev
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages || loading}>
              Next ›
            </button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AllUsersList;
